/**
 * A reply leaving the app: copied with its formatting, or exported as a
 * document. Copy writes HTML (for Word, Gmail, Docs) next to the markdown (for
 * plain-text targets); export and save hand the markdown to the server's docx
 * authoring, which takes the leading heading as the document title.
 */
import rehypeStringify from "rehype-stringify";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";

import { unwrapCodeLinks } from "@/features/agents/refs/ChatLink";
import { isAppPath } from "@/features/agents/refs";
import { ASSISTANT_PERSONA } from "@/features/agents/types";

const APP_LINK_RE = /\]\((\/[^)\s]*)/g;

/**
 * App links (`[Letter](/cases/…)`) made absolute, so they still go somewhere
 * once pasted into an email or a Word file.
 */
export const withAbsoluteLinks = (
  markdown: string,
  origin: string = window.location.origin
): string =>
  unwrapCodeLinks(markdown).replace(APP_LINK_RE, (match, href: string) =>
    isAppPath(href) ? `](${origin}${href}` : match
  );

type HastNode = {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

/** Word and Gmail drop class-based CSS; a table without inline borders pastes as loose text. */
const TABLE_STYLES: Record<string, string> = {
  table: "border-collapse:collapse",
  th: "border:1px solid silver;padding:4px 8px;text-align:left",
  td: "border:1px solid silver;padding:4px 8px"
};

const styleTables = () => (tree: HastNode) => {
  const walk = (node: HastNode) => {
    const style = node.type === "element" && TABLE_STYLES[node.tagName ?? ""];
    if (style) node.properties = { ...node.properties, style };
    node.children?.forEach(walk);
  };
  walk(tree);
};

// Raw HTML in model output is dropped: remark-rehype only passes it through
// with `allowDangerousHtml`, and there is no rehype-raw.
const processor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkRehype)
  .use(styleTables)
  .use(rehypeStringify);

export const markdownToHtml = (markdown: string): string =>
  String(processor.processSync(markdown));

/**
 * Copy with formatting: HTML for rich editors, markdown for everything else.
 * Synchronous up to the write so it stays inside the click's user gesture,
 * which Safari requires.
 */
export const copyRichText = async (markdown: string): Promise<void> => {
  const text = withAbsoluteLinks(markdown);
  const { clipboard } = navigator;
  if (typeof ClipboardItem !== "undefined" && clipboard?.write) {
    await clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([markdownToHtml(text)], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" })
      })
    ]);
    return;
  }
  await clipboard.writeText(text);
};

const LEADING_HEADING_RE = /^\s*#{1,6}[ \t]+(.+?)[ \t]*#*[ \t]*(?:\r?\n|$)/;

/** A heading's text without its inline markdown. */
const plainHeading = (text: string): string =>
  text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`~]/g, "")
    .trim();

export const fallbackExportTitle = (now: Date = new Date()): string =>
  `${ASSISTANT_PERSONA} Response, ${now.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  })}`;

/**
 * The document title and the body under it. The server prints the title as
 * the H1, so a leading heading moves into the title instead of printing twice.
 */
export const splitExportTitle = (
  markdown: string,
  now: Date = new Date()
): { title: string; body: string } => {
  const match = markdown.match(LEADING_HEADING_RE);
  const heading = match ? plainHeading(match[1]) : "";
  if (match && heading) {
    return { title: heading, body: markdown.slice(match[0].length).trim() };
  }
  return { title: fallbackExportTitle(now), body: markdown.trim() };
};
